export interface SP500Constituent {
  symbol: string;
  name: string;
  sector: string;
  subSector: string;
  headQuarter: string;
  dateFirstAdded: string;
  cik: string;
  founded: string;
}

export const WIKIPEDIA_API_URL =
  'https://en.wikipedia.org/w/api.php?action=parse&page=List_of_S%26P_500_companies&prop=wikitext&section=1&format=json';

// Sanity check — Wikipedia should always return close to 500+ companies
export const MIN_EXPECTED_CONSTITUENTS = 490;

/**
 * Extract display text from a wikilink like [[3M]] or [[AMD|Advanced Micro Devices]]
 */
export function extractWikilink(text: string): string {
  return text
    .replace(/\[\[([^\]]+)\]\]/g, (_link, inner: string) => inner.split('|').pop()!.trim())
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract ticker symbol from a template like {{NyseSymbol|MMM}} or {{NasdaqSymbol|AAPL}}
 */
export function extractTickerFromTemplate(text: string): string | null {
  const match = text.match(/\{\{(?:NyseSymbol|NasdaqSymbol|NYSE|NASDAQ|BZX link)\|([^}]+)\}\}/i);
  if (!match) return null;
  return match[1].trim();
}

/**
 * Parse the wikitext table from the Wikipedia S&P 500 page into SP500Constituent objects.
 */
export function parseWikitextTable(wikitext: string): SP500Constituent[] {
  const rows = wikitext.split(/^\s*\|-[^\n]*(?:\n|$)/m);
  const constituents: SP500Constituent[] = [];

  for (const row of rows) {
    const cells: string[] = [];
    for (const rawLine of row.split('\n')) {
      const line = rawLine.trim();
      if (line.startsWith('|}')) break;
      if (line.startsWith('|')) {
        // A leading | or || starts a cell; inline || starts another cell.
        // Single pipes inside wikilinks/templates belong to that cell.
        cells.push(
          ...line
            .replace(/^\|\|?\s*/, '')
            .split('||')
            .map((cell) => cell.trim()),
        );
      } else if (line && cells.length) {
        cells[cells.length - 1] += ` ${line}`;
      }
    }

    const symbol = extractTickerFromTemplate(cells[0] || '');
    if (!symbol) continue;
    // A recognized security row with missing/extra columns invalidates the
    // snapshot. Do not quietly cache a partial index after source format drift.
    if (cells.length !== 8) return [];

    constituents.push({
      symbol,
      name: extractWikilink(cells[1]),
      sector: extractWikilink(cells[2]),
      subSector: extractWikilink(cells[3]),
      headQuarter: extractWikilink(cells[4]),
      dateFirstAdded: cells[5],
      cik: cells[6],
      founded: cells[7].replace(/\|}.*$/, '').trim(),
    });
  }

  return constituents;
}

const GICS_SECTORS = new Set([
  'Communication Services',
  'Consumer Discretionary',
  'Consumer Staples',
  'Energy',
  'Financials',
  'Health Care',
  'Industrials',
  'Information Technology',
  'Materials',
  'Real Estate',
  'Utilities',
]);

/** Reject shifted columns and partial refreshes before serving or caching them. */
export function isValidSP500Constituents(value: unknown): value is SP500Constituent[] {
  if (!Array.isArray(value) || value.length < MIN_EXPECTED_CONSTITUENTS) return false;
  const symbols = new Set<string>();
  return value.every((row) => {
    if (!row || typeof row !== 'object') return false;
    const fields = [
      'symbol',
      'name',
      'sector',
      'subSector',
      'headQuarter',
      'dateFirstAdded',
      'cik',
      'founded',
    ];
    if (fields.some((field) => typeof row[field] !== 'string')) return false;
    if (!/^[A-Z][A-Z0-9.-]{0,14}$/.test(row.symbol) || symbols.has(row.symbol)) return false;
    symbols.add(row.symbol);
    return !!row.name.trim() && !/[{}\[\]]/.test(row.name) && GICS_SECTORS.has(row.sector);
  });
}
